import {
  Home, Briefcase, Users, Heart, Handshake, Truck, Store, UtensilsCrossed, Hotel,
  Hospital, GraduationCap, Landmark, Fence, HardHat, CalendarClock, MapPin,
} from 'lucide-react';

export const APP_NAME = 'AdresseSénégal';
export const APP_URL = typeof window !== 'undefined' ? window.location.origin : 'https://adressesn.tmconsultingf.com';

// ───────────────────────── Types de lieux (alignés sur le backend)
export const TYPES = [
  { id: 'maison',         label: 'Maison',          icon: Home },
  { id: 'bureau',         label: 'Bureau',          icon: Briefcase },
  { id: 'famille',        label: 'Famille',         icon: Users },
  { id: 'ami',            label: 'Ami',             icon: Heart },
  { id: 'client',         label: 'Client',          icon: Handshake },
  { id: 'fournisseur',    label: 'Fournisseur',     icon: Truck },
  { id: 'commerce',       label: 'Commerce',        icon: Store },
  { id: 'restaurant',     label: 'Restaurant',      icon: UtensilsCrossed },
  { id: 'hotel',          label: 'Hôtel',           icon: Hotel },
  { id: 'sante',          label: 'Santé',           icon: Hospital },
  { id: 'ecole',          label: 'École',           icon: GraduationCap },
  { id: 'administration', label: 'Administration',  icon: Landmark },
  { id: 'terrain',        label: 'Terrain',         icon: Fence },
  { id: 'chantier',       label: 'Chantier',        icon: HardHat },
  { id: 'rendez-vous',    label: 'Rendez-vous',     icon: CalendarClock },
  { id: 'autre',          label: 'Autre',           icon: MapPin },
];

const TYPES_PAR_ID = Object.fromEntries(TYPES.map(t => [t.id, t]));
export const getType = (id) => TYPES_PAR_ID[id] || TYPES_PAR_ID.autre;

// Raccourcis affichés sur l'accueil et en filtres
export const TYPES_RACCOURCIS = ['maison', 'bureau', 'famille', 'client', 'commerce', 'restaurant'];

// ───────────────────────── Formatage
export const coords = (lieu) => ({
  lat: parseFloat(lieu.latitude),
  lng: parseFloat(lieu.longitude),
});

export const formatCoords = (lieu) => {
  const { lat, lng } = coords(lieu);
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

export const localisation = (lieu) =>
  [lieu.quartier, lieu.ville && lieu.ville !== lieu.quartier ? lieu.ville : null].filter(Boolean).join(' · ') || lieu.ville || '';

export const lienPublic = (code) => `${APP_URL}/adresse/${encodeURIComponent(code)}`;

// ───────────────────────── Recherche tolérante (côté client)
export const normaliser = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Distance d'édition bornée (tolérance aux fautes de frappe)
const proche = (a, b, max) => {
  if (Math.abs(a.length - b.length) > max) return false;
  let prec = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cour = [i];
    let minLigne = i;
    for (let j = 1; j <= b.length; j++) {
      cour[j] = Math.min(prec[j] + 1, cour[j - 1] + 1, prec[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cour[j] < minLigne) minLigne = cour[j];
    }
    if (minLigne > max) return false;
    prec = cour;
  }
  return prec[b.length] <= max;
};

// Index de recherche calculé une fois par lieu
export const indexer = (lieu) => {
  const type = getType(lieu.type);
  const champs = {
    nom: normaliser(lieu.nom),
    cau: normaliser(lieu.code_cau),
    alias: normaliser((lieu.alias || []).join(' ')),
    lieu: normaliser([lieu.quartier, lieu.commune, lieu.ville].join(' ')),
    autres: normaliser([lieu.description, lieu.note, type.label, lieu.telephone].join(' ')),
  };
  const mots = new Set(Object.values(champs).join(' ').split(' ').filter(Boolean));
  return { champs, mots: [...mots] };
};

const POIDS = { nom: 10, cau: 9, alias: 8, lieu: 4, autres: 2 };

export const score = (index, requete) => {
  const q = normaliser(requete);
  if (!q) return 0;
  const termes = q.split(' ');
  let total = 0;
  for (const terme of termes) {
    let meilleur = 0;
    for (const [champ, valeur] of Object.entries(index.champs)) {
      if (!valeur) continue;
      if (valeur === q) meilleur = Math.max(meilleur, POIDS[champ] * 3);
      else if (valeur.startsWith(terme) || valeur.includes(` ${terme}`)) meilleur = Math.max(meilleur, POIDS[champ] * 2);
      else if (valeur.includes(terme)) meilleur = Math.max(meilleur, POIDS[champ]);
    }
    if (!meilleur && terme.length >= 4) {
      const max = terme.length >= 7 ? 2 : 1;
      if (index.mots.some(m => proche(m, terme, max))) meilleur = 1;
    }
    if (!meilleur) return 0; // tous les termes doivent correspondre
    total += meilleur;
  }
  return total;
};

export const rechercher = (lieuxIndexes, requete) => {
  if (!normaliser(requete)) return lieuxIndexes.map(x => x.lieu);
  return lieuxIndexes
    .map(x => ({ lieu: x.lieu, s: score(x.index, requete) }))
    .filter(x => x.s > 0)
    .sort((a, b) => b.s - a.s || (b.lieu.favori ? 1 : 0) - (a.lieu.favori ? 1 : 0))
    .map(x => x.lieu);
};

// ───────────────────────── Navigation (coordonnées GPS exactes, jamais le nom)
// Ajouter ici un futur moteur (navigation interne, OSRM, GraphHopper…)
export const NAVIGATEURS = [
  { id: 'google', label: 'Google Maps', url: ({ lat, lng }) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving` },
  { id: 'waze',   label: 'Waze',        url: ({ lat, lng }) => `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` },
];

export const ouvrirNavigation = (lieu, id = 'google') => {
  const nav = NAVIGATEURS.find(n => n.id === id) || NAVIGATEURS[0];
  window.open(nav.url(coords(lieu)), '_blank', 'noopener');
};

// ───────────────────────── Partage
export const messagePartage = (lieu) => {
  const loc = [lieu.quartier, lieu.ville].filter(Boolean).join(', ');
  return [
    `📍 ${lieu.nom || lieu.code_cau}`,
    '',
    `CAU : ${lieu.code_cau}`,
    loc,
    '',
    '🧭 Pour vous y rendre :',
    lienPublic(lieu.code_cau),
  ].filter((l, i, arr) => !(l === '' && arr[i - 1] === '')).join('\n');
};

export const partagerWhatsApp = (lieu) => {
  window.open(`https://wa.me/?text=${encodeURIComponent(messagePartage(lieu))}`, '_blank', 'noopener');
};

// Renvoie 'partage' | 'copie' | null
export const partager = async (lieu) => {
  const url = lienPublic(lieu.code_cau);
  const texte = messagePartage(lieu);
  if (navigator.share) {
    try {
      await navigator.share({ title: lieu.nom || lieu.code_cau, text: texte });
      return 'partage';
    } catch (e) {
      if (e && e.name === 'AbortError') return null;
    }
  }
  return (await copier(texte || url)) ? 'copie' : null;
};

export const copier = async (texte) => {
  try {
    await navigator.clipboard.writeText(texte);
    return true;
  } catch {
    try {
      const t = document.createElement('textarea');
      t.value = texte;
      t.style.position = 'fixed';
      t.style.opacity = '0';
      document.body.appendChild(t);
      t.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(t);
      return ok;
    } catch { return false; }
  }
};

// ───────────────────────── Lieux récents (stockés sur l'appareil)
const CLE_RECENTS = 'lieux_recents';

export const lireRecents = () => {
  try { return JSON.parse(localStorage.getItem(CLE_RECENTS)) || []; } catch { return []; }
};

export const ajouterRecent = (id) => {
  try {
    const liste = [id, ...lireRecents().filter(x => x !== id)].slice(0, 8);
    localStorage.setItem(CLE_RECENTS, JSON.stringify(liste));
  } catch { /* stockage indisponible : on ignore */ }
};
